// ─────────────────────────────────────────────────────────────────────────────
// OnsitePM Shared PDF Design System — jsPDF Helpers
// ─────────────────────────────────────────────────────────────────────────────
// All helpers accept a jsPDF `doc` instance as their first argument.
// Units: mm (jsPDF default for A4).
// ─────────────────────────────────────────────────────────────────────────────

import { COLORS, FONTS, SPACING, BRAND } from './tokens';

// ── Utility: hex color → [r, g, b] ──────────────────────────────────────────
const hexToRgb = (hex) => {
  const clean = hex?.replace('#', '');
  return [
    parseInt(clean?.substring(0, 2), 16),
    parseInt(clean?.substring(2, 4), 16),
    parseInt(clean?.substring(4, 6), 16),
  ];
};

// ── Utility: set fill color from hex ────────────────────────────────────────
const setFill = (doc, hex) => doc?.setFillColor(...hexToRgb(hex));

// ── Utility: set text color from hex ────────────────────────────────────────
const setTextColor = (doc, hex) => doc?.setTextColor(...hexToRgb(hex));

// ── Utility: set draw color from hex ────────────────────────────────────────
const setDrawColor = (doc, hex) => doc?.setDrawColor(...hexToRgb(hex));

// ── Content width helper (A4 = 210mm) ───────────────────────────────────────
const contentWidth = (doc) =>
  doc?.internal?.pageSize?.getWidth() - SPACING?.pageMargin * 2;

// ─────────────────────────────────────────────────────────────────────────────
// renderHeader
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Renders the document header band.
 *
 * @param {object} doc
 * @param {object} opts
 * @param {string|null} opts.logoBase64   - base64 data URL for the org logo (or null)
 * @param {string}      opts.orgName      - organization name
 * @param {string}      opts.docTitle     - main document title (large, right-aligned)
 * @param {string}      [opts.docSubtitle]- subtitle below title (small, right-aligned)
 * @param {Array<{label:string, value:string}>} [opts.metaLines] - meta rows below subtitle
 * @returns {number} Y position after the header (ready for next section)
 */
export const renderHeader = (doc, {
  logoBase64   = null,
  orgName      = '',
  docTitle     = '',
  docSubtitle  = '',
  metaLines    = [],
} = {}) => {
  const margin = SPACING?.pageMargin;
  const pageW  = doc?.internal?.pageSize?.getWidth();
  const cw     = contentWidth(doc);

  // ── Logo (left side, fit within 40×15 mm preserving natural aspect ratio) ─
  const logoMaxW = 40;
  const logoMaxH = 15;
  const logoY    = margin;
  let   logoEndX = margin; // track where logo ends for downstream layout

  if (logoBase64) {
    try {
      // Read natural dimensions to compute aspect-ratio-preserving size
      const props = doc?.getImageProperties(logoBase64);
      const naturalRatio = props?.width / props?.height;

      let w = logoMaxW;
      let h = w / naturalRatio;
      if (h > logoMaxH) {
        h = logoMaxH;
        w = h * naturalRatio;
      }

      // Center-align vertically within the logoMaxH bounding box
      const logoX = margin;
      const logoYCentered = logoY + (logoMaxH - h) / 2;

      doc?.addImage(logoBase64, 'PNG', logoX, logoYCentered, w, h, undefined, 'FAST');
      logoEndX = margin + w;
    } catch (_) {
      // logo failed — skip silently
    }
  }

  // ── Doc title (right-aligned) ────────────────────────────────────────────
  const rightX = margin + cw;
  let   curY   = margin;

  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(FONTS?.title?.size);
  setTextColor(doc, COLORS?.black);
  doc?.text(docTitle?.toUpperCase(), rightX, curY + 7, { align: 'right' });
  curY += 10;

  // ── Doc subtitle (right-aligned, small, gray) ────────────────────────────
  if (docSubtitle) {
    doc?.setFont('helvetica', 'normal');
    doc?.setFontSize(FONTS?.subtitle?.size);
    setTextColor(doc, COLORS?.gray500);
    doc?.text(docSubtitle?.toUpperCase(), rightX, curY, { align: 'right', charSpace: FONTS?.subtitle?.letterSpacing * 0.3 });
    curY += 5;
  }

  // ── Meta lines (right-aligned, label gray + value black bold) ───────────
  if (metaLines?.length > 0) {
    metaLines?.forEach(({ label, value }) => {
      doc?.setFont('helvetica', 'normal');
      doc?.setFontSize(FONTS?.small?.size);
      setTextColor(doc, COLORS?.gray500);
      doc?.text(`${label}: `, rightX, curY, { align: 'right' });

      const labelW = doc?.getTextWidth(`${label}: `);
      doc?.setFont('helvetica', 'bold');
      setTextColor(doc, COLORS?.black);
      doc?.text(value ?? '', rightX - labelW, curY, { align: 'right' });
      curY += 4.5;
    });
  }

  // ── Thin divider line at bottom of header ───────────────────────────────
  const headerBottom = Math.max(curY + 4, margin + SPACING?.headerHeight);
  doc?.setLineWidth(0.4);
  setDrawColor(doc, COLORS?.black);
  doc?.line(margin, headerBottom, margin + cw, headerBottom);

  return headerBottom + 4; // return Y ready for first section
};

// ─────────────────────────────────────────────────────────────────────────────
// renderFooter
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Renders a single centered footer line.
 *
 * @param {object} doc
 * @param {object} opts
 * @param {string} opts.orgName
 * @param {string} opts.lang          - 'fr' | 'en'
 * @param {number} opts.pageNum
 * @param {number} opts.totalPages
 */
export const renderFooter = (doc, {
  orgName    = '',
  lang       = 'fr',
  pageNum    = 1,
  totalPages = 1,
} = {}) => {
  const pageH  = doc?.internal?.pageSize?.getHeight();
  const pageW  = doc?.internal?.pageSize?.getWidth();
  const footerY = pageH - 8;

  const powered  = BRAND?.poweredBy?.[lang]  ?? BRAND?.poweredBy?.fr;
  const pageText = BRAND?.pageLabel?.[lang]
    ? BRAND?.pageLabel?.[lang](pageNum, totalPages)
    : BRAND?.pageLabel?.fr(pageNum, totalPages);

  const text = [orgName, powered, pageText]?.filter(Boolean)?.join(' · ');

  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(FONTS?.micro?.size);
  setTextColor(doc, COLORS?.gray500);
  doc?.text(text, pageW / 2, footerY, { align: 'center' });
};

// ─────────────────────────────────────────────────────────────────────────────
// renderSectionTitle
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Renders a bold uppercase section label with a thin underline.
 *
 * @param {object} doc
 * @param {string} label
 * @param {number} y      - current Y position
 * @returns {number}        new Y position after label + underline + gap
 */
export const renderSectionTitle = (doc, label, y) => {
  const margin = SPACING?.pageMargin;
  const cw     = contentWidth(doc);

  doc?.setFont('helvetica', 'bold');
  doc?.setFontSize(FONTS?.sectionLabel?.size);
  setTextColor(doc, COLORS?.black);
  doc?.text(label?.toUpperCase(), margin, y);

  const underlineY = y + 2;
  doc?.setLineWidth(0.4);
  setDrawColor(doc, COLORS?.gray300);
  doc?.line(margin, underlineY, margin + cw, underlineY);

  return underlineY + SPACING?.sectionGap * 0.6;
};

// ─────────────────────────────────────────────────────────────────────────────
// renderMetaRow
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Renders a two-column mini row: label (gray) + value (black bold).
 *
 * @param {object} doc
 * @param {number} x
 * @param {number} y
 * @param {string} label
 * @param {string} value
 */
export const renderMetaRow = (doc, x, y, label, value) => {
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(FONTS?.small?.size);
  setTextColor(doc, COLORS?.gray500);
  doc?.text(`${label}:`, x, y);

  const labelW = doc?.getTextWidth(`${label}: `);
  doc?.setFont('helvetica', 'bold');
  setTextColor(doc, COLORS?.black);
  doc?.text(String(value ?? ''), x + labelW, y);
};
